import type {Test} from '@/shared/types/api';

/**
 * Whether a result can produce a downloadable PDF, and the ELIS order id to ask
 * for when it can.
 *
 * Report PDFs live in ELIS and are fetched by `elis_id`. OHD screening results
 * are synthesized by the API from `ohd_result_sets` with `elis_id: null` (see
 * buildOhdOrder in warehouse.ts) — they are genuine results, but no ELIS order
 * exists behind them, so `/api/order/pdf` can only ever reject them. Offering
 * the download anyway produced a "please try again later" dialog on a retry
 * that could never succeed.
 */
export interface PdfAvailability {
    available: boolean;
    /** Present only when available; the id to POST to /api/order/pdf. */
    elisId?: number;
    /** Present only when unavailable; safe to show to a patient. */
    reason?: string;
}

const NO_LAB_ORDER_REASON = 'A PDF is not available for this screening. Contact your program administrator for a copy.';

export function resolvePdfAvailability(result: Test | null, realResult: Test | null): PdfAvailability {
    // The real record wins when the view is showing a stand-in.
    const elisId = realResult?.elis_id || result?.elis_id;

    if (typeof elisId !== 'number' || !Number.isFinite(elisId) || elisId <= 0) {
        return {available: false, reason: NO_LAB_ORDER_REASON};
    }

    return {available: true, elisId};
}

export const DEFAULT_DOWNLOAD_ERROR = 'Unable to download the PDF right now. Please try again shortly.';

/**
 * Pull the API's explanation out of a failed download. The request uses
 * responseType 'blob', so an error body arrives as a Blob rather than parsed
 * JSON — without decoding it the UI could only ever show one generic sentence,
 * which is how a permanently-unavailable PDF came to say "try again later".
 */
async function blobToText(blob: Blob): Promise<string> {
    // Blob.text() is missing on Safari < 14 (and in jsdom), where falling back
    // to the generic message would defeat the point of this whole path.
    if (typeof blob.text === 'function') return blob.text();
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
    });
}

export async function readDownloadError(error: unknown): Promise<string> {
    const data = (error as {response?: {data?: unknown}})?.response?.data;
    try {
        if (typeof Blob !== 'undefined' && data instanceof Blob) {
            const parsed = JSON.parse(await blobToText(data)) as {error?: string};
            if (parsed.error) return parsed.error;
        } else if (data && typeof data === 'object' && typeof (data as {error?: string}).error === 'string') {
            return (data as {error: string}).error;
        }
    } catch {
        // Non-JSON body (a proxy error page, say) — fall through to the default.
    }
    return DEFAULT_DOWNLOAD_ERROR;
}
